import { useEffect, useMemo, useState } from "react";
import { geoPath } from "d3-geo";
import { feature, mesh } from "topojson-client";
import statesTopo from "us-atlas/states-albers-10m.json";
import api from "../lib/api";
import { toast } from "sonner";
import { PageHeader, Card, money, Th, Td, DateRangeFilter, rangeParams } from "../components/shared";
import { useAuth } from "../context/AuthContext";

const path = geoPath();
const STATES = feature(statesTopo, statesTopo.objects.states).features;
const BORDERS = path(mesh(statesTopo, statesTopo.objects.states, (a, b) => a !== b));
const SCALE = ["#eef2ff", "#c7d2fe", "#a5b4fc", "#818cf8", "#6366f1", "#4f46e5", "#3730a3"];

const shade = (v, max) => (v ? SCALE[Math.min(SCALE.length - 1, 1 + Math.floor((v / max) * (SCALE.length - 2)))] : "#e2e8f0");

const Stat = ({ label, value, testId }) => (
  <Card className="p-4">
    <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
    <div data-testid={testId} className="font-heading text-2xl font-bold text-slate-900 mt-1">{value}</div>
  </Card>
);

const UsMap = ({ byState, metric, onHover, hovered }) => {
  const max = Math.max(1, ...Object.values(byState).map((s) => s[metric]));
  return (
    <svg viewBox="0 0 975 610" className="w-full h-auto max-h-[520px]" data-testid="us-map">
      {STATES.map((f) => {
        const s = byState[f.properties.name];
        return (
          <path
            key={f.id}
            d={path(f)}
            data-testid={`map-state-${f.properties.name.replace(/\s+/g, "-").toLowerCase()}`}
            fill={hovered === f.properties.name ? "#f59e0b" : shade(s?.[metric] || 0, max)}
            className="transition-colors cursor-pointer"
            onMouseEnter={() => onHover(f.properties.name)}
            onMouseLeave={() => onHover(null)}
            onClick={() => onHover(f.properties.name)}
          />
        );
      })}
      <path d={BORDERS} fill="none" stroke="#fff" strokeWidth={0.8} />
    </svg>
  );
};

const HoverCard = ({ name, s }) => (
  <div data-testid="map-hover-card" className="absolute bottom-10 left-3 bg-white/95 backdrop-blur border border-slate-200 rounded-lg shadow-sm px-3 py-2 text-xs min-w-[170px]">
    <div className="font-semibold text-slate-900 mb-1">{name}</div>
    <div className="flex justify-between gap-4"><span className="text-slate-500">Clicks</span><b>{s?.clicks || 0}</b></div>
    <div className="flex justify-between gap-4"><span className="text-emerald-600">Real US</span><span>{s?.real_us || 0}</span></div>
    <div className="flex justify-between gap-4"><span className="text-rose-600">VPN / Proxy</span><span>{s?.vpn || 0}</span></div>
    <div className="flex justify-between gap-4"><span className="text-slate-500">Conversions</span><b>{s?.conversions || 0}</b></div>
    <div className="flex justify-between gap-4"><span className="text-slate-500">Revenue</span><span className="text-emerald-600 font-mono">{money(s?.revenue)}</span></div>
  </div>
);

export default function ClickMap() {
  const { user } = useAuth();
  const [rangeSel, setRangeSel] = useState({ range: "today" });
  const [metric, setMetric] = useState("clicks");
  const [hovered, setHovered] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user?.is_owner) return;
    api.get(`/map/states?${rangeParams(rangeSel)}`).then((r) => setData(r.data)).catch(() => toast.error("Could not load map data"));
  }, [rangeSel, user]);

  const byState = useMemo(() => Object.fromEntries((data?.states || []).map((s) => [s.state, s])), [data]);

  if (!user?.is_owner) return <div data-testid="map-owner-only" className="text-sm text-slate-500">This page is only available to the account owner.</div>;
  const t = data?.totals;
  const btn = (m, l) => (
    <button key={m} data-testid={`map-metric-${m}`} onClick={() => setMetric(m)}
      className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${metric === m ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>
  );

  return (
    <div data-testid="click-map-page">
      <PageHeader title="Click Map" subtitle="Where your clicks and conversions came from, by US state (all clicks with a US location, including VPN)" />
      <div className="flex flex-wrap items-center gap-3 mb-4 fade-up">
        <DateRangeFilter value={rangeSel} onChange={setRangeSel} />
        <div className="inline-flex gap-1 bg-white border border-slate-200 rounded-lg p-1">{btn("clicks", "Clicks")}{btn("conversions", "Conversions")}{btn("revenue", "Revenue")}</div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat label="Clicks with US location" value={t ? `${t.us_located.toLocaleString()} / ${t.clicks.toLocaleString()}` : "…"} testId="map-us-located" />
        <Stat label="Not checked yet" value={t ? t.unchecked.toLocaleString() : "…"} testId="map-unchecked" />
        <Stat label="US conversions" value={t ? t.conversions.toLocaleString() : "…"} testId="map-conversions" />
        <Stat label="US revenue" value={t ? money(t.revenue) : "…"} testId="map-revenue" />
      </div>
      <div className="grid lg:grid-cols-[1fr_420px] gap-4">
        <Card className="p-4 relative">
          <UsMap byState={byState} metric={metric} onHover={setHovered} hovered={hovered} />
          {hovered && <HoverCard name={hovered} s={byState[hovered]} />}
          <div className="flex items-center gap-1 mt-2 text-[10px] text-slate-400">
            <span>None</span><span className="h-2 w-6 rounded-sm" style={{ background: "#e2e8f0" }} /><span className="ml-2">Fewer</span>{SCALE.slice(1).map((c) => <span key={c} className="h-2 w-6 rounded-sm" style={{ background: c }} />)}<span>More</span>
          </div>
        </Card>
        <Card className="overflow-hidden">
          <div className="max-h-[560px] overflow-auto">
            <table className="w-full">
              <thead><tr><Th>State</Th><Th className="text-right">Clicks</Th><Th className="text-right">VPN</Th><Th className="text-right">Conv</Th><Th className="text-right">Revenue</Th></tr></thead>
              <tbody data-testid="map-state-table">
                {(data?.states || []).map((s) => (
                  <tr key={s.state} data-testid={`map-row-${s.state.replace(/\s+/g, "-").toLowerCase()}`} className={`border-t border-slate-100 ${hovered === s.state ? "bg-amber-50" : ""}`} onMouseEnter={() => setHovered(s.state)} onMouseLeave={() => setHovered(null)}>
                    <Td className="text-xs font-medium">{s.state}</Td>
                    <Td className="text-right font-mono text-xs">{s.clicks}</Td>
                    <Td className="text-right font-mono text-xs text-rose-600">{s.vpn || "—"}</Td>
                    <Td className="text-right font-mono text-xs">{s.conversions || "—"}</Td>
                    <Td className="text-right font-mono text-xs text-emerald-600">{s.revenue ? money(s.revenue) : "—"}</Td>
                  </tr>
                ))}
                {data && data.states.length === 0 && <tr><td colSpan={5} data-testid="map-empty" className="text-center text-xs text-slate-400 py-8">No US-located clicks in this range yet</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
